import * as Y from 'yjs';

import { BaseError, type BaseErrorOptions } from '@canvas/error';

export class InvalidYjsUpdateError extends BaseError {
  constructor(
    readonly update: Uint8Array,
    options?: BaseErrorOptions,
  ) {
    super({ name: 'InvalidYjsUpdateError', ...options }, 'YJS update is invalid');
  }
}

/**
 * checks if an update is valid
 * the method this uses to validate likely has sub-optimal performance
 *
 * @param update a YJS update, Uint8Array
 * @returns boolean, true if the update is valid
 */
export function isValidUpdate(update: Uint8Array): boolean {
  try {
    Y.decodeUpdate(update);
    return true;
  } catch (err) {
    return false;
  }
}

/**
 * throws if an update is invalid
 *
 * @param update a YJS update, Uint8Array
 */
export function validUpdateOrDie(update: Uint8Array): void {
  if (!isValidUpdate(update)) {
    throw new InvalidYjsUpdateError(update);
  }
}

/**
 * like Y.applyUpdate but throws a more readable error message
 *
 * @param ydoc Y.Doc
 * @param update the update to apply
 * @param transactionOrigin (optional) see Y.applyUpdate()
 */
export function applyUpdate(
  ydoc: Y.Doc,
  update: Uint8Array,
  transactionOrigin?: any,
): void {
  try {
    Y.applyUpdate(ydoc, update, transactionOrigin);
  } catch (err) {
    throw new InvalidYjsUpdateError(update);
  }
}

/**
 * like Y.applyUpdate but safely no-ops if given an invalid (undecodable) `update` Uint8Array
 *
 * @param ydoc Y.Doc
 * @param update the update to apply
 * @param transactionOrigin (optional) see Y.applyUpdate()
 * @param onError (optional) a callback that is called with an error when invalid
 * @returns boolean, true if the update was applied
 */
export function safeApplyUpdate(
  ydoc: Y.Doc,
  update: Uint8Array,
  transactionOrigin?: any,
  onError?: (e: InvalidYjsUpdateError) => void,
): boolean {
  try {
    // please ensure that the only errors that can be thrown from this try
    // are instances of InvalidYjsUpdateError
    validUpdateOrDie(update);
    applyUpdate(ydoc, update, transactionOrigin);
    return true;
  } catch (err) {
    onError?.(err as InvalidYjsUpdateError);
    return false;
  }
}
